/**
 * NBT cart savings presentation helpers.
 *
 * Don Price / dj-giardco
 *
 * Integration boundary:
 * - Read-only calculations from Platter's existing Alpine cart store.
 * - Does not add, remove, apply, or recalculate Shopify discounts.
 * - Does not change bundle, gift-with-purchase, upsell, quantity, or checkout behavior.
 * - All currency values stay in Shopify's integer minor units (cents).
 */
(() => {
  "use strict";

  // Don Price / dj-giardco: Avoid replacing an existing helper if Theme Editor
  // hot reload evaluates this asset more than once.
  if (window.djGiardcoCartSavings) return;

  const toNumber = (value, fallback = 0) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
  };

  const cleanText = (value, fallback = "") => {
    const text = String(value ?? "").trim();
    return text || fallback;
  };

  const normalizeDiscountApplication = (allocation) =>
    allocation?.discount_application ?? allocation ?? {};

  const getCartState = (cartStore) => cartStore?.state ?? {};

  const getMerchandiseTotal = (cartState) => {
    const calculatedOriginalTotal = (cartState?.items ?? []).reduce(
      (total, item) =>
        total +
        toNumber(item?.quantity) *
          toNumber(item?.selling_plan_allocation?.compare_at_price ?? item?.original_price),
      0,
    );

    return Math.max(
      0,
      toNumber(
        cartState?.original_pre_selling_plan_total_price ??
          cartState?.original_total_price ??
          cartState?.items_subtotal_price,
        calculatedOriginalTotal,
      ),
    );
  };

  const getSubtotal = (cartState) => Math.max(0, toNumber(cartState?.total_price));

  const getSavings = (cartState) =>
    Math.max(0, getMerchandiseTotal(cartState) - getSubtotal(cartState));

  const getDiscountApplications = (cartState) => {
    const cartApplications = (cartState?.cart_level_discount_applications ?? []).map(
      normalizeDiscountApplication,
    );
    const lineApplications = (cartState?.items ?? []).flatMap((item) =>
      (item?.line_level_discount_allocations ?? []).map(normalizeDiscountApplication),
    );

    return [...cartApplications, ...lineApplications].filter(Boolean);
  };

  const getDiscountPercentage = (discountApplication) => {
    if (discountApplication?.value_type === "percentage") {
      return Math.max(0, toNumber(discountApplication?.value));
    }

    // Don Price / dj-giardco: Some Shopify Functions expose the percentage
    // only in the customer-facing title. This is a display fallback only.
    const titlePercentage = cleanText(discountApplication?.title).match(/(\d+(?:\.\d+)?)\s*%/);
    return titlePercentage ? Math.max(0, toNumber(titlePercentage[1])) : 0;
  };

  const readConfiguration = (element) => {
    const dataset = element?.dataset ?? {};
    const tiers = [
      {
        target: toNumber(dataset.tierOneTarget, 20000),
        percent: toNumber(dataset.tierOnePercent, 10),
      },
      {
        target: toNumber(dataset.tierTwoTarget, 30000),
        percent: toNumber(dataset.tierTwoPercent, 20),
      },
      {
        target: toNumber(dataset.tierThreeTarget, 40000),
        percent: toNumber(dataset.tierThreePercent, 30),
      },
    ]
      .filter((tier) => tier.target > 0 && tier.percent > 0)
      .sort((tierA, tierB) => tierA.target - tierB.target);

    return {
      tiers,
      promotionKeyword: cleanText(dataset.promotionKeyword, "Anniversary"),
      promotionLabel: cleanText(dataset.promotionLabel, "Anniversary savings"),
      fallbackSavingsLabel: cleanText(dataset.fallbackSavingsLabel, "Applied savings"),
    };
  };

  window.djGiardcoCartSavings = {
    /**
     * Don Price / dj-giardco: Returns Alpine-friendly computed properties.
     * Keeping the cart store reference intact lets Alpine update the display
     * after every native cart mutation without adding a second listener.
     */
    create(element, cartStore) {
      const configuration = readConfiguration(element);

      return {
        cartStore,
        configuration,

        get cartState() {
          return getCartState(this.cartStore);
        },

        get merchandiseTotal() {
          return getMerchandiseTotal(this.cartState);
        },

        get subtotal() {
          return getSubtotal(this.cartState);
        },

        get savings() {
          return getSavings(this.cartState);
        },

        get discountApplications() {
          return getDiscountApplications(this.cartState);
        },

        get promotionDiscountApplications() {
          const promotionKeyword = this.configuration.promotionKeyword.toLowerCase();

          return this.discountApplications.filter((discountApplication) =>
            cleanText(discountApplication?.title).toLowerCase().includes(promotionKeyword),
          );
        },

        get hasNonPromotionDiscounts() {
          const promotionKeyword = this.configuration.promotionKeyword.toLowerCase();

          return this.discountApplications.some(
            (discountApplication) =>
              !cleanText(discountApplication?.title).toLowerCase().includes(promotionKeyword),
          );
        },

        get appliedPromotionPercentage() {
          return this.promotionDiscountApplications.reduce(
            (highestPercentage, discountApplication) =>
              Math.max(highestPercentage, getDiscountPercentage(discountApplication)),
            0,
          );
        },

        get qualifiedTier() {
          return (
            [...this.configuration.tiers]
              .reverse()
              .find((tier) => this.merchandiseTotal >= tier.target) ?? null
          );
        },

        get nextTier() {
          return (
            this.configuration.tiers.find((tier) => this.merchandiseTotal < tier.target) ?? null
          );
        },

        get topTier() {
          return this.configuration.tiers[this.configuration.tiers.length - 1] ?? null;
        },

        get currentTierPercentage() {
          if (this.savings <= 0) return 0;

          // Don Price / dj-giardco: A free gift can legitimately carry a 100%
          // line discount. Only the configured promotion may label a 10/20/30
          // tier as unlocked, so unrelated gifts never override this badge.
          return (
            this.appliedPromotionPercentage ||
            (this.promotionDiscountApplications.length ? this.qualifiedTier?.percent : 0) ||
            0
          );
        },

        get amountToNextTier() {
          return this.nextTier ? Math.max(0, this.nextTier.target - this.merchandiseTotal) : 0;
        },

        get progressPercentage() {
          if (!this.topTier?.target) return 0;
          if (this.savings > 0 && this.currentTierPercentage <= 0) return 0;
          return Math.min(100, Math.max(0, (this.merchandiseTotal / this.topTier.target) * 100));
        },

        get badgeText() {
          if (this.savings > 0 && this.currentTierPercentage > 0) {
            return `${this.currentTierPercentage}% OFF UNLOCKED`;
          }

          if (this.promotionDiscountApplications.length && this.qualifiedTier) {
            return `${this.qualifiedTier.percent}% OFF QUALIFIED`;
          }

          if (this.savings > 0) return "SAVINGS APPLIED";
          return this.nextTier ? `NEXT: ${this.nextTier.percent}% OFF` : "BEST PRICE";
        },

        get kickerText() {
          if (
            this.savings > 0 &&
            this.topTier &&
            this.currentTierPercentage >= this.topTier.percent
          ) {
            return "TOP TIER";
          }

          if (this.savings > 0 && this.currentTierPercentage > 0) {
            return `${this.currentTierPercentage}% TIER`;
          }

          return this.savings > 0 ? "CART SAVINGS" : "SAVINGS STATUS";
        },

        get supportText() {
          if (this.savings > 0 && this.currentTierPercentage > 0) {
            return "Best price applied automatically. No code needed.";
          }
          if (this.savings > 0) return "Applied offers are reflected in your subtotal.";
          if (this.qualifiedTier) return "Your qualifying price is updating automatically.";
          return "Your cart updates automatically. No code needed.";
        },

        get savingsLabel() {
          const matchingPromotion = this.discountApplications.find((discountApplication) =>
            cleanText(discountApplication?.title)
              .toLowerCase()
              .includes(this.configuration.promotionKeyword.toLowerCase()),
          );
          const percentageSuffix = matchingPromotion && this.appliedPromotionPercentage
            ? ` (${this.appliedPromotionPercentage}%)`
            : "";

          if (matchingPromotion && this.hasNonPromotionDiscounts) {
            const tierSuffix = this.appliedPromotionPercentage
              ? ` (${this.appliedPromotionPercentage}% tier)`
              : "";

            return `Total savings${tierSuffix}`;
          }

          return matchingPromotion
            ? `${this.configuration.promotionLabel}${percentageSuffix}`
            : `${this.configuration.fallbackSavingsLabel}${percentageSuffix}`;
        },

        isTierQualified(target) {
          const tier = this.configuration.tiers.find(
            (configuredTier) => configuredTier.target === toNumber(target),
          );

          // Don Price / dj-giardco: Once savings exist, checkmarks represent
          // the configured promotion actually unlocked, not spend alone. This
          // prevents excluded products or unrelated offers from showing false
          // 10/20/30 completion states.
          if (this.savings > 0) {
            return !!tier && this.currentTierPercentage >= tier.percent;
          }

          return this.merchandiseTotal >= toNumber(target);
        },
      };
    },
  };
})();
